'use client';

import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	Grid,
	LinearProgress,
	MenuItem,
	TextField,
} from '@mui/material';
import { gql, useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import { PhoneNumberInput } from '@parkchirp/shared-ui-components';
import { InverseButton } from '@parkchirp/ui-library';
import { cloneDeep } from 'lodash';
import {
	AccessDeviceInactiveReasonEnum,
	AccessDeviceStatusEnum,
	AppNames,
	ParkerAccessSystemStatusEnumDisplay,
	ParkerSuspensionStatusEnum,
	ParkerTerminationStatusEnum,
} from 'parkchirp-shared/enums';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CREATE_PARKER, UPDATE_PARKER } from '../../../mutations';
import {
	GET_FACILITY_V2,
	GET_ORGANIZATION_PARKER_TYPES_BY_FACILITY_ID,
	GET_PARKER_FOR_PARKER_DETAIL,
	GET_TENANT_ENABLED_ACCESS_DEVICES,
} from '../../../queries';
import type { SetShowSnackbar } from '../../../types';
import {
	Dayjs,
	dayjs,
	formatDateForAccessDevices,
	formatDateParkerStatusForDisplay,
	getDayjsFromDeviceDate,
	isOwnerPortal,
	isValidEmail,
	isValidPostalCode,
	provinceFieldOptions,
	stateFieldOptions,
} from '../../../utils';
import { RateDetail } from '../../rates';
import { RateAndSublotHistoryTables } from '../rateAndSublotHistory';
import { AccessDevicesDetail } from './accessDevicesDetail';
import { ActionsDetail } from './actionsDetail';
import { IntegrationDetail } from './integrationDetail';
import { StatusDetail } from './statusDetail';
import { validateMinimumVehicleLimit, validateVehicleLimit } from './vehicleDetailConfig';
import { VehiclesDetail } from './vehiclesDetail';

const GET_PARKER_START_DATES = gql`
    query GetParkerStartDates(
        $parkerId: String!,
        $tenantSubscriptionId: String
    ) {
        getParkerStartDates(
            parkerId: $parkerId,
            tenantSubscriptionId: $tenantSubscriptionId
        ) {
            billingStartDate
        }
    }
`;

const GET_SUBSCRIPTION = gql`
    query GetSubscription(
        $subscriptionId: String!,
        $tenantSubscriptionId: String
    ) {
        getSubscription(
            subscriptionId: $subscriptionId,
            tenantSubscriptionId: $tenantSubscriptionId
        ) {
            accountId
            subscriptionName
            billingProfileId
            tenantReservedSpaceEnabled
			accessSystemMeta
            billingProfile {
                terminationNoticeDay
                terminationRule
                billingDay
            }
        }
    }
`;

type BillingProfile = {
	terminationRule: string;
	terminationNoticeDay: number;
	billingDay: number;
};

type SubscriptionData = {
	getSubscription: {
		accountId: number;
		subscriptionName: string;
		billingProfileId: string;
		accessSystemMeta: {
			tibaMType: number;
		};
		tenantReservedSpaceEnabled: boolean;
		billingProfile: BillingProfile;
		parkerTypeName: string;
	};
};

type OrganizationParkerType = {
	id: string;
	parkerTypeName: string;
	isDefault: boolean;
};

type FacilityData = {
	getFacility: {
		facilityName: string;
		facilityNumber: number;
		accessSystemCredentials: {
			tibaActiveMType: number;
			barcodeRequired?: boolean;
		};
		facilitySettings: {
			usesDefaultRates: boolean;
			maxActiveVehicles: number | null;
			minActiveVehicles: number | null;
		};
		integration: string;
		timezone: string;
		sublots: {
			id: number;
			name: string;
			primaryLot: boolean;
		}[];
	};
};

type DeviceData = {
	id: string;
	type: string;
	status: string;
	dateAdded: string;
	deviceId: string;
	dateActive: string;
	dateInactive: string;
	inactiveReason: string;
	isNew: boolean;
	hasInactiveDate: boolean;
};

type ParkerData = {
	getParker: {
		subscriptionId: string;
		firstName: string;
		lastName: string;
		address: string;
		city: string;
		state: string;
		zip: string;
		phone: string;
		countryCode: number;
		email: string;
		group: string;
		reservedSpace: string;
		rateId: string;
		suspensionStatus: string;
		terminationStatus: string;
		accessSystemStatus: string;
		parkerAccessDevices: DeviceData[];
		accessSystemMeta: any;
		pool: string;
		passback: boolean;
		sublotId: number;
		sequenceNumber: number;
		activatedDate: string;
		availableTenantRateSublots: {
			sublot: {
				id: number;
				name: string;
			};
		}[];
		isSoldOut: boolean;
		parkerTypeName: string;
	};
};

type StartDatesData = {
	getParkerStartDates: {
		billingStartDate: string;
	};
};

interface ParkerDetailModalProps {
	parkerId?: string;
	parkerFirstName?: string;
	parkerLastName?: string;
	parkerAccountId?: string;
	subscriptionId?: string | string[];
	parkerSubscriptionName?: string;
	facilityId?: string;
	parkerFacilityName?: string;
	parkerFacilityNumber?: string;
	handleClose: () => void;
	setShowSnackbar: SetShowSnackbar;
	theme: any;
	tenantSubscriptionId?: string;
	refetchSubscriptionData?: () => void;
	refetchParkersFromCache?: () => void;
	canDoLessThan50DefaultDollarRateFee?: boolean;
	isParkerTypeRequired?: boolean;
}

const getPrimarySublot = (facilityData) => {
	const sublots = facilityData?.getFacility?.sublots;
	return sublots?.find((lot) => lot.primaryLot)?.id.toString() || '';
};

export const normalizeStateValue = (input: string): string | undefined => {
	if (!input) return '';

	const allOptions = [...stateFieldOptions, ...provinceFieldOptions];

	const match = allOptions.find(
		(opt) => opt.value.toLowerCase() === input.toLowerCase() || opt.label.toLowerCase() === input.toLowerCase(),
	);

	return match?.value;
};

export const ParkerDetailModal = ({
	parkerId,
	parkerFirstName,
	parkerLastName,
	parkerAccountId,
	subscriptionId,
	parkerSubscriptionName,
	facilityId,
	parkerFacilityName,
	parkerFacilityNumber,
	handleClose,
	setShowSnackbar,
	theme,
	tenantSubscriptionId,
	refetchSubscriptionData = () => {},
	refetchParkersFromCache,
	canDoLessThan50DefaultDollarRateFee,
	isParkerTypeRequired,
}: ParkerDetailModalProps) => {
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [currentFormState, setCurrentFormState] = useState<Record<string, any> | null>(null);
	const [newFirstName, setNewFirstName] = useState<string | null>();
	const [newLastName, setNewLastName] = useState<string | null>();
	const [newAddress, setNewAddress] = useState<string | null>();
	const [newCity, setNewCity] = useState<string | null>();
	const [newState, setNewState] = useState<string | null>();
	const [newZip, setNewZip] = useState<string | null>();

	const [newPhone, setNewPhone] = useState<string | null>();
	const [newPhoneCountryCode, setNewPhoneCountryCode] = useState<number>(1);
	const [newPhoneValid, setNewPhoneValid] = useState<boolean>(false);
	const [newEmail, setNewEmail] = useState<string | null>();
	const [newGroup, setNewGroup] = useState<string | null>();
	const [newReservedSpace, setNewReservedSpace] = useState<string | null>();

	const [newRateId, setNewRateId] = useState<string | null>('');
	const [sublotId, setSublotId] = useState<string>('');
	const [newBillingStartDate, setNewBillingStartDate] = useState<Dayjs>(dayjs.utc());
	const [selectedRateIsSoldOut, setSelectedRateIsSoldOut] = useState<boolean>(false);

	const [newPool, setNewPool] = useState<string | null>();
	const [newPassback, setNewPassback] = useState<boolean | null>(null);

	const [newAccessDevices, setNewAccessDevices] = useState<DeviceData[] | null>([]);

	const [newVehicles, setNewVehicles] = useState<any[]>([]);
	const [persistedVehicleRows, setPersistedVehicleRows] = useState<any[]>([]);

	const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
	const [refreshKey, setRefreshKey] = useState<number>(0);
	const [isUpdateMode, setIsUpdateMode] = useState<boolean>(false);
	const [existingSubscriptionId, setExistingSubscriptionId] = useState<string | null>(null);
	const [availableSublots, setAvailableSublots] = useState([]);
	const [selectedOrganizationParkerTypeId, setSelectedOrganizationParkerTypeId] = useState<string>('');

	const [confirmationMessage, setConfirmationMessage] = useState<JSX.Element>(null);
	const [showConfirm, setShowConfirm] = useState<boolean>(false);
	const [sequenceNumber, setSequenceNumber] = useState<number>(null);
	const [activatedDate, setActivatedDate] = useState<string | null>(null);

	const [monthlyFeePrice, setMonthlyFeePrice] = useState<number | null>(null);
	const [saving, setSaving] = useState<boolean>(false);
	const [tibaActiveMType, setTibaActiveMType] = useState<string>('');
	const newMode = !parkerId;
	const shouldRequireParkerType = !!isParkerTypeRequired && (newMode || !tenantSubscriptionId);

	const [vehicleHistoryChanged, setVehicleHistoryChanged] = useState<boolean>(false);

	const mapVehiclesForState = useCallback((vehicles: any[] = []) => {
		const todayStart = dayjs.utc().startOf('day');

		return vehicles.map((v: any) => {
			if (!v.license) {
				v.needsLicense = true;
			}
			v.isPastEndDateFromDb = !!v.endDate && dayjs.utc(v.endDate).isBefore(todayStart);
			v.hasEndDate = !!v.endDate;
			v.yearFromDb = v.year;
			return v;
		});
	}, []);

	const handleVehiclesLoaded = useCallback((vehicleRows: any[]) => {
		setPersistedVehicleRows(cloneDeep(vehicleRows));
		setCurrentFormState((prevState) =>
			prevState
				? {
						...prevState,
						vehicleRows: cloneDeep(vehicleRows),
					}
				: prevState,
		);
	}, []);

	const [getParker, { data: parkerData, loading: parkerLoading }] = useLazyQuery<ParkerData>(
		GET_PARKER_FOR_PARKER_DETAIL,
		{
			variables: {
				parkerId,
				tenantSubscriptionId,
			},
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			onError: () => {
				setShowSnackbar({
					message: 'Failed to fetch parker data',
					autoHideDuration: 5000,
					severity: 'error',
				});
			},
			fetchPolicy: 'network-only',
		},
	);

	const [getParkerStartDates, { data: startDatesData }] = useLazyQuery<StartDatesData>(GET_PARKER_START_DATES);

	const { data: facilityData, loading: facilityLoading } = useQuery<FacilityData>(GET_FACILITY_V2, {
		variables: {
			facilityId,
			withSublots: true,
			tenantSubscriptionId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
	});

	const { data: tenantAccessDevices, loading: tenantDevicesLoading } = useQuery(GET_TENANT_ENABLED_ACCESS_DEVICES, {
		variables: {
			subscriptionId,
			tenantSubscriptionId,
		},
		skip: !tenantSubscriptionId,
		fetchPolicy: 'network-only',
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});

	// TODO: PCA-9957/PCA-9958 - Retrieve default Parker Type and auto-select in dropdown
	const { data: organizationParkerTypesData } = useQuery(GET_ORGANIZATION_PARKER_TYPES_BY_FACILITY_ID, {
		variables: {
			facilityId,
		},
		context: {
			apiVersion: 'v2',
			appName: tenantSubscriptionId ? AppNames.TENANT_PORTAL : AppNames.ADMIN_DASHBOARD,
		},
		fetchPolicy: 'network-only',
	});
	const organizationParkerTypes: OrganizationParkerType[] =
		organizationParkerTypesData?.getOrganizationParkerTypesByFacilityId || [];
	const hasOrganizationParkerTypes = organizationParkerTypes.length > 0;
	let isParkerTypeDisabled = true;
	if (!isEditMode) {
		isParkerTypeDisabled = true;
	} else if (tenantSubscriptionId) {
		if (newMode && hasOrganizationParkerTypes) {
			isParkerTypeDisabled = false;
		} else {
			isParkerTypeDisabled = true;
		}
	} else if (hasOrganizationParkerTypes) {
		isParkerTypeDisabled = false;
	}

	const sublots = facilityData?.getFacility?.sublots;
	const usesDefaultRates = !!facilityData?.getFacility?.facilitySettings?.usesDefaultRates;
	const maxActiveVehicles = facilityData?.getFacility?.facilitySettings?.maxActiveVehicles;
	const minActiveVehicles = facilityData?.getFacility?.facilitySettings?.minActiveVehicles;
	const primarySublot = getPrimarySublot(facilityData);

	const getAccessDevicesList = useCallback((parkerAccessDevices: any) => {
		const accessDevicesList = new Map<string, DeviceData>();

		// NOTE: Flatten the Access Device List to pull the device type to root object
		if (parkerAccessDevices?.length) {
			parkerAccessDevices?.forEach(
				({ accessDeviceType, id, dateAdded, dateActive, dateInactive, inactiveReason, deviceId, status }) => {
					accessDevicesList.set(id, {
						id,
						dateAdded: dateAdded ? formatDateParkerStatusForDisplay(dateAdded) : dateAdded,
						dateInactive: dateInactive ? formatDateParkerStatusForDisplay(dateInactive) : dateInactive,
						inactiveReason: inactiveReason?.replace(/_/g, ' '),
						deviceId,
						status,
						type: accessDeviceType.type,
						isNew: false,
						hasInactiveDate: !!dateInactive,
						dateActive: formatDateParkerStatusForDisplay(dateActive),
					});
				},
			);

			return [...accessDevicesList.values()];
		}
		return [];
	}, []);

	useEffect(() => {
		if (parkerData?.getParker) {
			setNewFirstName(parkerData.getParker.firstName);
			setNewLastName(parkerData.getParker.lastName);
			setNewAddress(parkerData.getParker.address);
			setNewCity(parkerData.getParker.city);
			setNewState(parkerData.getParker.state);
			setNewZip(parkerData.getParker.zip);
			setNewPhone(parkerData.getParker.phone);
			setNewPhoneCountryCode(parkerData.getParker.countryCode || 1);
			setNewEmail(parkerData.getParker.email);
			setNewGroup(parkerData.getParker.group);
			setNewReservedSpace(parkerData.getParker.reservedSpace);
			setNewRateId(parkerData.getParker.rateId);
			setSelectedRateIsSoldOut(parkerData.getParker.isSoldOut);
			setNewPool(parkerData.getParker.pool);
			setNewPassback(parkerData.getParker.passback);
			setIsEditMode(false);
			if (process.env.NEXT_PUBLIC_APP_NAME === 'TENANT_PORTAL') {
				const mappedSublots = parkerData?.getParker?.availableTenantRateSublots.map((sel) => ({
					...sel.sublot,
				}));
				setAvailableSublots(mappedSublots);
				setSublotId(parkerData.getParker.sublotId?.toString() || null);
			} else {
				setAvailableSublots(sublots);
				setSublotId(parkerData.getParker.sublotId?.toString() || primarySublot);
			}
			setExistingSubscriptionId(parkerData.getParker.subscriptionId);
			setSequenceNumber(parkerData.getParker.sequenceNumber);
			setActivatedDate(parkerData.getParker.activatedDate);
			if (parkerData.getParker.accessSystemMeta?.tibaActiveMType) {
				setTibaActiveMType(String(parkerData.getParker.accessSystemMeta.tibaActiveMType));
			}
			const parkerTypeMatch = organizationParkerTypes.find(
				(type) => type.parkerTypeName === parkerData.getParker.parkerTypeName,
			);
			setSelectedOrganizationParkerTypeId(parkerTypeMatch?.id || '');
			const deviceRows = getAccessDevicesList(parkerData.getParker?.parkerAccessDevices);
			setNewAccessDevices(deviceRows);

			setCurrentFormState({
				firstName: parkerData.getParker.firstName,
				lastName: parkerData.getParker.lastName,
				address: parkerData.getParker.address,
				city: parkerData.getParker.city,
				state: parkerData.getParker.state,
				zip: parkerData.getParker.zip,
				phone: parkerData.getParker.phone,
				countryCode: parkerData.getParker.countryCode,
				email: parkerData.getParker.email,
				group: parkerData.getParker.group,
				reservedSpace: parkerData.getParker.reservedSpace,
				rateId: parkerData.getParker.rateId,
				pool: parkerData.getParker.pool,
				passback: parkerData.getParker.passback,
				sublotId: parkerData.getParker.sublotId?.toString() || primarySublot,
				subscriptionId: parkerData.getParker.subscriptionId,
				sequenceNumber: parkerData.getParker.sequenceNumber,
				deviceRows,
			});
		} else if (subscriptionId) {
			// If there's no parker and a sub id then we are in new mode
			if (process.env.NEXT_PUBLIC_APP_NAME === 'TENANT_PORTAL') {
				setSublotId(null);
			} else {
				setAvailableSublots(sublots);
				setSublotId(primarySublot);
			}
			setIsEditMode(true);
			setSelectedOrganizationParkerTypeId('');
		}
	}, [getAccessDevicesList, organizationParkerTypes, parkerData, primarySublot, subscriptionId, sublots]);

	const {
		suspensionStatus = 'PENDING',
		terminationStatus = 'PENDING',
		accessSystemStatus = 'OFF_PENDING',
	} = parkerData?.getParker ?? {};
	const isWaitlistParker = suspensionStatus === 'WAITLIST';
	const parkerIsPending =
		ParkerSuspensionStatusEnum[suspensionStatus] === ParkerSuspensionStatusEnum.PENDING ||
		ParkerTerminationStatusEnum[terminationStatus] === ParkerTerminationStatusEnum.PENDING;

	useEffect(() => {
		if (parkerId) {
			getParkerStartDates({
				variables: {
					parkerId,
					tenantSubscriptionId,
				},
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
				fetchPolicy: 'network-only',
			});
		}
	}, [parkerId, getParkerStartDates, tenantSubscriptionId]);

	const startDateString = startDatesData?.getParkerStartDates?.billingStartDate;
	const oldBillingStartDate = useMemo(
		() => (startDateString ? dayjs(startDateString, 'YYYY-MM-DD') : null),
		[startDateString],
	);

	useEffect(() => {
		if (oldBillingStartDate) {
			setNewBillingStartDate(oldBillingStartDate);
			setIsEditMode(false);
		}
	}, [oldBillingStartDate]);

	useEffect(() => {
		if (parkerId) {
			getParker({ variables: { parkerId, tenantSubscriptionId } });
		}
	}, [parkerId, getParker, tenantSubscriptionId]);

	const { data: subscriptionData, loading: subscriptionLoading } = useQuery<SubscriptionData>(GET_SUBSCRIPTION, {
		variables: {
			subscriptionId,
			tenantSubscriptionId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
	});

	const { accountId, subscriptionName, billingProfileId, billingProfile, accessSystemMeta } =
		subscriptionData?.getSubscription || {};
	const {
		facilityName,
		facilityNumber,
		timezone: facilityTimezone,
		integration: facilityIntegration,
		accessSystemCredentials,
	} = facilityData?.getFacility || {};

	const currentDate = useMemo(() => {
		const formatFacilityDate = new Date(dayjs().tz(facilityTimezone).format('YYYY-MM-DD HH:mm:ss'));
		const current = new Date(
			formatFacilityDate.getFullYear(),
			formatFacilityDate.getMonth(),
			formatFacilityDate.getDate(),
		);
		return current;
	}, [facilityTimezone]);

	const showPoolPassback = facilityIntegration === 'TIBA' || facilityIntegration === 'FLASH_PARC';

	const [createParker, { loading: createLoading }] = useMutation(CREATE_PARKER, {
		onCompleted: (data) => {
			const hasWarning = data?.createParker?.parkerWarningMessage;
			const message = hasWarning ?? 'Parker created';
			setShowSnackbar({
				message,
				severity: hasWarning ? 'warning' : 'success',
				autoHideDuration: 8000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setHasUnsavedChanges(false);
			handleClose();
		},
		onError: (error) => {
			setShowSnackbar({
				message: error.message,
				autoHideDuration: 10000,
				severity: 'error',
			});
		},
	});

	const [updateParker, { loading: updateLoading }] = useMutation(UPDATE_PARKER, {
		onCompleted: (data) => {
			const message = data?.updateParker?.surchargeWarning || data?.updateParker?.parkerWarningMessage;
			const vehicles = data?.updateParker?.parkerVehicles;

			if (vehicles) {
				const mappedVehicles = mapVehiclesForState(vehicles);
				setNewVehicles(mappedVehicles);
				setPersistedVehicleRows(cloneDeep(mappedVehicles));
				setCurrentFormState((prevState) =>
					prevState
						? {
								...prevState,
								vehicleRows: cloneDeep(mappedVehicles),
							}
						: prevState,
				);
			}

			if (message) {
				setShowSnackbar({
					message,
					severity: 'warning',
					autoHideDuration: 6000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			} else {
				setShowSnackbar({
					message: 'Parker updated',
					severity: 'success',
					autoHideDuration: 3000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
			setHasUnsavedChanges(false);
			setIsEditMode(false);
			setIsUpdateMode(false);
		},
		onError: (error) => {
			setIsEditMode(true);
			setShowSnackbar({
				message: error.message,
				autoHideDuration: 10000,
				severity: 'error',
			});
		},
		refetchQueries: [
			{
				query: GET_PARKER_FOR_PARKER_DETAIL,
				variables: {
					parkerId,
					tenantSubscriptionId,
				},
			},
			'getParker',
		],
	});

	const hasAllRequiredFields = useCallback(() => {
		function validateEmail() {
			return isValidEmail(newEmail);
		}

		const requiredFields = [
			{ label: 'First Name', value: newFirstName },
			{ label: 'Last Name', value: newLastName },
			{ label: 'Rate', value: newRateId },
			{
				label: 'Billing Start Date',
				value: newBillingStartDate?.toString() || currentDate.toString(),
			},
			{ label: 'Sublot', value: sublotId },
			...(shouldRequireParkerType ? [{ label: 'Parker Type', value: selectedOrganizationParkerTypeId }] : []),
		];
		const missingFields = requiredFields.filter(({ value }) => ['', null, undefined].includes(value));
		if (missingFields.length) {
			const missingAsString = missingFields?.map(({ label }) => label).join(', ');
			setShowSnackbar({
				message: `Unable to save due to missing field(s): ${missingAsString}`,
				severity: 'error',
				autoHideDuration: 6000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}

		const vehicleLimitValidation = validateVehicleLimit({
			vehicles: newVehicles,
			maxActiveVehicles,
		});
		const minimumVehicleValidation =
			newMode && minActiveVehicles
				? validateMinimumVehicleLimit({
						vehicles: newVehicles,
						minActiveVehicles,
						effectiveStartDate: newBillingStartDate?.toString() || currentDate.toString(),
					})
				: { isValid: true, reason: '' };

		const additionalValidations = [
			{
				message: 'Phone number is invalid',
				failingCondition: newPhone && !newPhoneValid,
			},
			{
				message: 'No Monthly Fee Price',
				failingCondition: usesDefaultRates && monthlyFeePrice === null && newMode && !selectedRateIsSoldOut,
			},
			{
				message: 'Monthly Fee Price cannot be less than $50',
				failingCondition:
					usesDefaultRates &&
					monthlyFeePrice < 50 &&
					newMode &&
					(canDoLessThan50DefaultDollarRateFee === false || tenantSubscriptionId),
			},
			{
				message: `Invalid email address`,
				failingCondition: newEmail && !validateEmail(),
			},
			{
				message: `Please enter a valid zip code`,
				failingCondition: newZip && !isValidPostalCode(newZip),
			},
			{
				message: `Please select a device type`,
				failingCondition: newAccessDevices?.some((device) => !device.type),
			},
			{
				message: `Device Inactive Date cannot be before Device Active Date`,
				failingCondition:
					newAccessDevices?.length &&
					newAccessDevices.some((device) => {
						const { dateActive, dateInactive } = device;
						return dateInactive && dayjs(dateActive).isAfter(dayjs(dateInactive));
					}),
			},
			{
				message: `Please select a device inactive reason`,
				failingCondition: newAccessDevices?.some((device) => {
					const { dateInactive, inactiveReason } = device;
					return dateInactive && !inactiveReason;
				}),
			},
			{
				message: `Please provide a device ID`,
				failingCondition: newAccessDevices?.some((device) => {
					const { type, deviceId, isNew } = device;
					return isNew && type !== 'Barcode' && !deviceId;
				}),
			},
			{
				message: `You must add at least one vehicle to the parker for the Get My Parking integration.`,
				failingCondition: facilityIntegration === 'GET_MY_PARKING' && !newVehicles.length,
			},
			{
				message: `All vehicles must have a license plate for the Get My Parking integration.`,
				failingCondition:
					facilityIntegration === 'GET_MY_PARKING' && newVehicles.some((v) => v.isNew && !v.license),
			},
			{
				message: `All vehicles must have a make and model`,
				failingCondition: newVehicles.some((v) => !v.make || !v.model),
			},
			{
				message: `A barcode must be active on or before the parker's start date for the Get My Parking integration.`,
				failingCondition:
					facilityIntegration === 'GET_MY_PARKING' &&
					accessSystemCredentials?.barcodeRequired &&
					(!newAccessDevices?.length || // No devices at all
						!newAccessDevices.some((device) => device.type === 'Barcode') || // No Barcode
						!newAccessDevices.some(
							(device) =>
								device.type === 'Barcode' &&
								device.dateActive &&
								dayjs(device.dateActive).isSameOrBefore(newBillingStartDate, 'day'),
						)), // No Barcode with startDate <= newBillingStartDate
			},
			{
				message: vehicleLimitValidation.reason,
				failingCondition: !vehicleLimitValidation.isValid,
			},
			{
				message: minimumVehicleValidation.reason,
				failingCondition: !minimumVehicleValidation.isValid,
			},
		];
		const failingValidations = additionalValidations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				autoHideDuration: 6000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [
		newFirstName,
		newLastName,
		newRateId,
		newBillingStartDate,
		currentDate,
		newPhone,
		newEmail,
		newZip,
		newAccessDevices,
		sublotId,
		setShowSnackbar,
		newPhoneValid,
		facilityIntegration,
		newVehicles,
		usesDefaultRates,
		monthlyFeePrice,
		newMode,
		minActiveVehicles,
		shouldRequireParkerType,
		selectedOrganizationParkerTypeId,
		accessSystemCredentials?.barcodeRequired,
		canDoLessThan50DefaultDollarRateFee,
		maxActiveVehicles,
		selectedRateIsSoldOut,
		tenantSubscriptionId,
	]);

	const handleParkerSubmit = useCallback(async () => {
		if (hasAllRequiredFields()) {
			try {
				await createParker({
					context: {
						apiVersion: 'v2',
					},
					variables: {
						tenantSubscriptionId,
						rateId: newRateId,
						monthlyFeePrice: monthlyFeePrice ? Math.round(Number(monthlyFeePrice) * 100) : null,
						subscriptionId,
						startDate: newBillingStartDate.format('YYYY-MM-DD'),
						parkerVehicles: newVehicles?.map((vehicle) => ({
							license: vehicle.license ? vehicle.license.toUpperCase() : null,
							make: vehicle.make?.name ?? 'OTHER',
							model: vehicle.model?.name ?? 'Other',
							year: vehicle.year,
							state: vehicle.state,
							color: vehicle.color,
							decal: vehicle.decal,
							modelId: vehicle.model?.id,
							startDate: vehicle?.startDate,
							endDate: vehicle?.endDate,
						})),
						sublotId: parseInt(sublotId, 10),
						parker: {
							firstName: newFirstName,
							lastName: newLastName,
							email: newEmail,
							address: newAddress,
							city: newCity,
							state: newState,
							zip: newZip,
							phone: newPhone,
							countryCode: newPhoneCountryCode,
							group: newGroup,
							pool: newPool,
							passback: newPassback,
							reservedSpace: newReservedSpace,
							parkerAccessDevices: newAccessDevices?.map((device) => ({
								type: device.type,
								deviceId: device.deviceId,
								status: Object.keys(AccessDeviceStatusEnum).find(
									(key) => AccessDeviceStatusEnum[key] === device.status,
								),
								dateAdded: formatDateForAccessDevices(device.dateAdded),
								dateActive: formatDateForAccessDevices(device.dateActive),
								dateInactive: formatDateForAccessDevices(device.dateInactive) || null,
								inactiveReason: Object.keys(AccessDeviceInactiveReasonEnum).find(
									(key) => AccessDeviceInactiveReasonEnum[key] === device.inactiveReason,
								),
							})),
							tibaActiveMType:
								tibaActiveMType ||
								String(accessSystemMeta?.tibaMType) ||
								String(accessSystemCredentials?.tibaActiveMType) ||
								null,
							organizationParkerTypeId: selectedOrganizationParkerTypeId,
						},
					},
				});
				if (refetchParkersFromCache) {
					await refetchParkersFromCache();
				}
				refetchSubscriptionData();
			} catch (error) {
				console.log(error);
			}
		}
	}, [
		hasAllRequiredFields,
		createParker,
		tenantSubscriptionId,
		newRateId,
		subscriptionId,
		newBillingStartDate,
		newVehicles,
		sublotId,
		newAccessDevices,
		newFirstName,
		newLastName,
		newEmail,
		newAddress,
		newCity,
		newState,
		newZip,
		newPhone,
		newGroup,
		newPool,
		newPassback,
		newReservedSpace,
		refetchSubscriptionData,
		refetchParkersFromCache,
		monthlyFeePrice,
		newPhoneCountryCode,
		tibaActiveMType,
		selectedOrganizationParkerTypeId,
		accessSystemCredentials?.tibaActiveMType,
		accessSystemMeta?.tibaMType,
	]);

	const handleCancel = useCallback(() => {
		if (hasUnsavedChanges) {
			setShowSnackbar({
				message: 'There are unsaved changes on your page, press cancel again to discard changes.',
				severity: 'error',
				autoHideDuration: 6000,
				onClose: () => {
					setShowSnackbar(null);
					setHasUnsavedChanges(false);
				},
			});
		} else if (isUpdateMode) {
			setShowSnackbar(null);
			setIsEditMode(false);
			setIsUpdateMode(false);
			if (persistedVehicleRows.length) {
				setNewVehicles(cloneDeep(persistedVehicleRows));
			} else if (currentFormState?.vehicleRows) {
				setNewVehicles(cloneDeep(currentFormState.vehicleRows));
			} else {
				setNewVehicles((prevState) => prevState.filter((v) => !v.isNew));
			}
			setRefreshKey((prevKey) => prevKey + 1);

			if (currentFormState) {
				setNewFirstName(currentFormState.firstName);
				setNewLastName(currentFormState.lastName);
				setNewAddress(currentFormState.address);
				setNewCity(currentFormState.city);
				setNewState(currentFormState.state);
				setNewZip(currentFormState.zip);
				setNewPhone(currentFormState.phone);
				setNewPhoneCountryCode(currentFormState.countryCode);
				setNewEmail(currentFormState.email);
				setNewGroup(currentFormState.group);
				setNewReservedSpace(currentFormState.reservedSpace);
				setNewRateId(currentFormState.rateId);
				setNewPool(currentFormState.pool);
				setNewPassback(currentFormState.passback);
				setSublotId(currentFormState.sublotId?.toString() || primarySublot);
				setExistingSubscriptionId(currentFormState.subscriptionId);
				setNewAccessDevices(currentFormState.deviceRows);
				setSequenceNumber(currentFormState.sequenceNumber);
			}
			if (oldBillingStartDate) {
				setNewBillingStartDate(oldBillingStartDate);
			}
		} else {
			setShowSnackbar(null);
			handleClose();
		}
		setHasUnsavedChanges(false);
	}, [
		currentFormState,
		persistedVehicleRows,
		oldBillingStartDate,
		hasUnsavedChanges,
		handleClose,
		setShowSnackbar,
		isUpdateMode,
		primarySublot,
	]);

	const handleEditParker = useCallback(() => {
		setIsEditMode(true);
		setIsUpdateMode(true);
	}, []);

	const handleUpdateParker = useCallback(async () => {
		if (hasAllRequiredFields()) {
			try {
				if (vehicleHistoryChanged) {
					setShowSnackbar({
						message: 'Unsaved vehicle history changes will be lost. Save before continuing.',
						severity: 'info',
						onClose: () => {
							setVehicleHistoryChanged(false);
							setShowSnackbar(null);
						},
					});
					return;
				}
				await updateParker({
					context: {
						apiVersion: 'v2',
					},
					variables: {
						id: parkerId,
						tenantSubscriptionId,
						subscriptionId: existingSubscriptionId,
						rateId: newRateId,
						sublotId: parseInt(sublotId, 10),
						startDate: newBillingStartDate.format('YYYY-MM-DD'),
						parkerVehicles: newVehicles?.map((vehicle) => {
							const { isNew } = vehicle;
							const make = (isNew ? vehicle.make?.name : vehicle.make) || 'OTHER';
							const model = (isNew ? vehicle.model?.name : vehicle.model) || 'Other';
							return {
								...(!isNew && { id: vehicle.id }),
								license: vehicle.license,
								make,
								model,
								state: vehicle.state,
								color: vehicle.color,
								decal: vehicle.decal,
								year: vehicle.year,
								modelId: vehicle.model.id,
								startDate: vehicle.startDate,
								endDate: vehicle.endDate,
							};
						}),
						parker: {
							id: parkerId,
							firstName: newFirstName,
							lastName: newLastName,
							email: newEmail,
							address: newAddress,
							city: newCity,
							state: newState,
							zip: newZip,
							phone: newPhone,
							countryCode: newPhoneCountryCode,
							group: newGroup,
							pool: newPool,
							passback: newPassback,
							reservedSpace: newReservedSpace,
							parkerAccessDevices: newAccessDevices?.map((device) => ({
								...(!device.isNew && { id: device.id }),
								type: device.type,
								deviceId: device.isNew && device.type === 'Barcode' ? '' : device.deviceId,
								status: Object.keys(AccessDeviceStatusEnum).find(
									(key) => AccessDeviceStatusEnum[key] === device.status,
								),
								dateAdded: formatDateForAccessDevices(device.dateAdded),
								dateActive: formatDateForAccessDevices(device.dateActive),
								dateInactive: formatDateForAccessDevices(device.dateInactive) || null,
								inactiveReason: Object.keys(AccessDeviceInactiveReasonEnum).find(
									(key) => AccessDeviceInactiveReasonEnum[key] === device.inactiveReason,
								),
							})),
							tibaActiveMType: tibaActiveMType || String(accessSystemMeta?.tibaMType),
							organizationParkerTypeId: selectedOrganizationParkerTypeId,
						},
					},
				});
				setHasUnsavedChanges(false);
				if (refetchParkersFromCache) {
					await refetchParkersFromCache();
				}
			} catch (error) {
				setShowSnackbar({
					message: error.message,
					severity: 'error',
					autoHideDuration: 5000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
		}
	}, [
		hasAllRequiredFields,
		newVehicles,
		newAddress,
		newCity,
		newState,
		newPhone,
		newFirstName,
		newLastName,
		newBillingStartDate,
		newEmail,
		newZip,
		newGroup,
		newPool,
		newPassback,
		newReservedSpace,
		existingSubscriptionId,
		newAccessDevices,
		updateParker,
		setShowSnackbar,
		parkerId,
		newRateId,
		sublotId,
		tenantSubscriptionId,
		refetchParkersFromCache,
		newPhoneCountryCode,
		tibaActiveMType,
		vehicleHistoryChanged,
		selectedOrganizationParkerTypeId,
		accessSystemMeta?.tibaMType,
	]);

	const handleSave = useCallback(
		async () => (isUpdateMode ? handleUpdateParker() : handleParkerSubmit()),
		[handleParkerSubmit, handleUpdateParker, isUpdateMode],
	);

	const determineWarnings = useCallback(() => {
		const warnings: string[] = [];
		const deviceActiveBeforeBilling = newAccessDevices.some(({ dateActive: deviceDateActive }) => {
			const formattedDateActive = getDayjsFromDeviceDate(deviceDateActive);
			return formattedDateActive.isBefore(newBillingStartDate, 'day');
		});
		const curDate = dayjs(currentDate).startOf('day');
		const startDate = dayjs(newBillingStartDate).tz(facilityTimezone).startOf('day');
		const dateDiff = startDate.diff(curDate, 'day');
		const startDateExceedsFutureLimit = dateDiff > 90;
		const startDateExceedsPastLimit = dateDiff < -90;

		const changesMadeToBillingStartDate =
			oldBillingStartDate && newBillingStartDate ? !oldBillingStartDate.isSame(newBillingStartDate) : true;

		if (deviceActiveBeforeBilling) {
			warnings.push("You have set an access device to activate before the parker's billing start date.");
		}
		if (changesMadeToBillingStartDate && startDateExceedsFutureLimit) {
			warnings.push('The billing start date is more than 90 days in the future.');
		}
		if (changesMadeToBillingStartDate && startDateExceedsPastLimit) {
			warnings.push('The billing start date is more than 90 days in the past.');
		}
		return warnings;
	}, [currentDate, facilityTimezone, newAccessDevices, newBillingStartDate, oldBillingStartDate]);

	const getMinStartDate = useCallback(() => {
		if (tenantSubscriptionId) {
			return dayjs().startOf('day');
		}
		if (suspensionStatus === 'NOT_INVOICED' && terminationStatus === 'NOT_INVOICED') {
			const startOfMonth = newBillingStartDate.startOf('month');
			return startOfMonth;
		}
		return null;
	}, [newBillingStartDate, suspensionStatus, terminationStatus, tenantSubscriptionId]);

	const minStartDate = getMinStartDate();

	const handleSaveClick = useCallback(async () => {
		setSaving(true);
		const warnings = determineWarnings();
		if (!warnings.length) {
			await handleSave();
			setSaving(false);
		} else {
			setConfirmationMessage(
				<>
					{warnings.map((warning, i) => (
						<>
							{warning}
							{i < warnings.length - 1 && <br />}
						</>
					))}
				</>,
			);
			setShowConfirm(true);
			setSaving(false);
		}
	}, [determineWarnings, handleSave]);

	const handleConfirm = useCallback(async () => {
		setShowConfirm(false);
		await handleSave();
	}, [handleSave]);

	const isReservedSpaceDisabled =
		tenantSubscriptionId && !subscriptionData?.getSubscription?.tenantReservedSpaceEnabled;

	const activatedOnDate: string = useMemo(() => {
		if (parkerLoading || !activatedDate) {
			return 'N/A';
		}
		if (
			ParkerSuspensionStatusEnum.PENDING === suspensionStatus &&
			ParkerTerminationStatusEnum.PENDING === terminationStatus
		) {
			return '-';
		}
		return dayjs(activatedDate).tz(facilityTimezone).format('MM/DD/YYYY hh:mm a');
	}, [activatedDate, facilityTimezone, parkerLoading, suspensionStatus, terminationStatus]);

	return (
		<>
			<Box className='parker-detail'>
				{((parkerLoading && !parkerData) ||
					((subscriptionLoading || facilityLoading) && (!subscriptionData || !facilityData))) && (
					<LinearProgress sx={{ zIndex: 101 }} data-cy='linear-progress-indicator' />
				)}
				<Grid
					className='header-status'
					container
					spacing={4}
					sx={{
						padding: '0 30px 30px',
						position: 'sticky',
						top: -15,
						zIndex: 100,
						backgroundColor: '#FFFFFF',
					}}
				>
					<Grid item xs={6}>
						<Box className='parker-header' sx={{ backgroundColor: '#FFFFFF' }}>
							<h1 data-cy='parker-name-text'>
								{parkerFirstName ? `${parkerFirstName} ${parkerLastName}` : 'New Parker'}
							</h1>
							<p data-cy='account-id-text'>Account ID: AC-{parkerAccountId || accountId}</p>
							<p data-cy='subscription-text'>
								Subscription: {parkerSubscriptionName || subscriptionName}
							</p>
							<p data-cy='facility-text'>
								Facility: {parkerFacilityName || facilityName} ({parkerFacilityNumber || facilityNumber}
								)
							</p>
							<p data-cy='activated-on-text'>Activated On: {activatedOnDate}</p>
						</Box>
					</Grid>
					<Grid item xs={6} display='flex' justifyContent='flex-end' sx={{ mt: 2, fontSize: '14px' }}>
						{updateLoading || parkerLoading ? (
							<CircularProgress size={25} />
						) : (
							<Box className='status-container' sx={{ border: '1px solid', padding: '20px 20px' }}>
								<p data-cy='suspension-status-text'>
									Suspension Status: {ParkerSuspensionStatusEnum[suspensionStatus]}
								</p>
								<p data-cy='termination-status-text'>
									Termination Status: {ParkerTerminationStatusEnum[terminationStatus]}
								</p>
								<p data-cy='access-system-status-text'>
									Access System Status: {ParkerAccessSystemStatusEnumDisplay[accessSystemStatus]}
								</p>
							</Box>
						)}
					</Grid>
				</Grid>
				<Grid
					className='input-container'
					container
					spacing={4}
					sx={{ mt: 2, padding: '0 20px', overflowY: 'auto', flexGrow: 1 }}
				>
					<Grid item xs={6}>
						<TextField
							required
							id='first-name'
							label='First Name'
							placeholder='First Name'
							value={newFirstName || ''}
							type='text'
							style={{ width: '100%' }}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setNewFirstName(e.target.value);
							}}
							InputProps={{
								readOnly: !isEditMode,
							}}
							inputProps={{
								'data-cy': 'parker-first-name-input',
							}}
						/>
						<TextField
							required
							id='last-name'
							label='Last Name'
							placeholder='Last Name'
							value={newLastName || ''}
							type='text'
							style={{ width: '100%', marginTop: '20px' }}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setNewLastName(e.target.value);
							}}
							InputProps={{
								readOnly: !isEditMode,
							}}
							inputProps={{
								'data-cy': 'parker-last-name-input',
							}}
						/>
						<TextField
							id='address'
							label='Address'
							placeholder='Address'
							value={newAddress || ''}
							type='text'
							style={{ width: '100%', marginTop: '20px' }}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setNewAddress(e.target.value);
							}}
							InputProps={{
								readOnly: !isEditMode,
							}}
							inputProps={{
								'data-cy': 'parker-address-input',
							}}
						/>
						<Grid container spacing={2}>
							<Grid item xs={4}>
								<TextField
									id='city'
									label='City'
									placeholder='City'
									value={newCity || ''}
									type='text'
									style={{ width: '100%', marginTop: '20px' }}
									onChange={(e) => {
										setHasUnsavedChanges(true);
										setNewCity(e.target.value);
									}}
									InputProps={{
										readOnly: !isEditMode,
									}}
									inputProps={{
										'data-cy': 'parker-city-input',
									}}
								/>
							</Grid>
							<Grid item xs={4}>
								<TextField
									id='state'
									select
									label='State/Province'
									value={normalizeStateValue(newState) || ''}
									style={{ width: '100%', marginTop: '20px' }}
									onChange={(e) => {
										setHasUnsavedChanges(true);
										setNewState(e.target.value);
									}}
									InputProps={{
										readOnly: !isEditMode,
									}}
									data-cy='parker-state-input'
								>
									{[...stateFieldOptions, ...provinceFieldOptions]
										.sort((a, b) => a.value.localeCompare(b.value))
										.map((option) => (
											<MenuItem key={option.value} value={option.value}>
												{option.value}
											</MenuItem>
										))}
								</TextField>
							</Grid>
							<Grid item xs={4}>
								<TextField
									id='zip'
									label='ZIP'
									placeholder='zip code'
									value={newZip || ''}
									type='text'
									style={{ width: '100%', marginTop: '20px' }}
									onChange={(e) => {
										setHasUnsavedChanges(true);
										setNewZip(e.target.value);
									}}
									InputProps={{
										readOnly: !isEditMode,
									}}
									inputProps={{
										'data-cy': 'parker-zip-code-input',
									}}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={6}>
						<PhoneNumberInput
							phoneNumber={newPhone}
							countryCode={newPhoneCountryCode || 1}
							readOnly={!isEditMode}
							onPhoneChange={(phone) => {
								setHasUnsavedChanges(true);
								setNewPhone(phone);
							}}
							onCountryCodeChange={(code) => {
								setHasUnsavedChanges(true);
								setNewPhoneCountryCode(code);
							}}
							onValidityChange={(isValid) => {
								setNewPhoneValid(isValid);
							}}
						/>
						<TextField
							id='email'
							label='Email'
							placeholder='Email'
							value={newEmail || ''}
							type='text'
							style={{ width: '100%', marginTop: '20px' }}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setNewEmail(e.target.value);
							}}
							InputProps={{
								readOnly: !isEditMode,
							}}
							inputProps={{
								'data-cy': 'parker-email-input',
							}}
						/>
						<TextField
							id='group'
							label='Group'
							placeholder='Group'
							value={newGroup || ''}
							type='text'
							style={{ width: '100%', marginTop: '20px' }}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								setNewGroup(e.target.value);
							}}
							InputProps={{
								readOnly: !isEditMode,
							}}
							inputProps={{
								'data-cy': 'parker-group-input',
							}}
						/>
						<Grid container spacing={4}>
							<Grid item xs={sequenceNumber ? 4 : 6}>
								<TextField
									id='reserved-space'
									label='Reserved Space'
									placeholder='Reserved Space'
									value={newReservedSpace || ''}
									type='text'
									style={{ width: '100%', marginTop: '20px' }}
									onChange={(e) => {
										setHasUnsavedChanges(true);
										setNewReservedSpace(e.target.value);
									}}
									disabled={!subscriptionLoading && isReservedSpaceDisabled}
									InputProps={{
										readOnly: !isEditMode || subscriptionLoading,
									}}
									inputProps={{
										'data-cy': 'parker-reserved-space-input',
									}}
								/>
							</Grid>
							{sequenceNumber && (
								<Grid item xs={4}>
									<TextField
										id='sequence-number'
										label='Sequence Number'
										value={sequenceNumber}
										type='text'
										style={{ width: '100%', marginTop: '20px' }}
										InputProps={{
											readOnly: true,
										}}
									/>
								</Grid>
							)}
							<Grid item xs={sequenceNumber ? 4 : 6}>
								<TextField
									id='parker-type'
									select
									required={shouldRequireParkerType}
									label='Parker Type'
									value={selectedOrganizationParkerTypeId || ''}
									style={{ width: '100%', marginTop: '20px' }}
									onChange={(e) => {
										setSelectedOrganizationParkerTypeId(e.target.value);
									}}
									disabled={isParkerTypeDisabled}
									inputProps={{
										'data-cy': 'parker-type-input',
									}}
								>
									<MenuItem value=''>
										<em>None</em>
									</MenuItem>
									{organizationParkerTypes.map((organizationParkerType) => (
										<MenuItem key={organizationParkerType.id} value={organizationParkerType.id}>
											{organizationParkerType.parkerTypeName}
										</MenuItem>
									))}
								</TextField>
							</Grid>
						</Grid>
					</Grid>
					{!isWaitlistParker && (
						<RateDetail
							parkerId={parkerId}
							subscriptionId={subscriptionId}
							newRateId={newRateId}
							setNewRateId={setNewRateId}
							newBillingStartDate={newBillingStartDate}
							setNewBillingStartDate={setNewBillingStartDate}
							setHasUnsavedChanges={setHasUnsavedChanges}
							isEditMode={isEditMode}
							sublots={availableSublots}
							sublotId={sublotId}
							setSublotId={setSublotId}
							suspensionStatus={suspensionStatus}
							terminationStatus={terminationStatus}
							tenantSubscriptionId={tenantSubscriptionId}
							facilityTimezone={facilityTimezone}
							parkerLoading={parkerLoading}
							usesDefaultRates={usesDefaultRates}
							setMonthlyFeePrice={setMonthlyFeePrice}
							monthlyFeePrice={monthlyFeePrice}
							newMode={newMode}
							facilityId={facilityId}
							minStartDate={minStartDate}
							facilityLoading={facilityLoading}
							setSelectedRateIsSoldOut={setSelectedRateIsSoldOut}
							selectedRateIsSoldOut={selectedRateIsSoldOut}
						/>
					)}
					{!newMode && !isWaitlistParker && (
						<RateAndSublotHistoryTables
							parkerId={parkerId}
							billingProfileId={billingProfileId}
							terminationStatus={terminationStatus}
							billingStartDate={newBillingStartDate}
							parkerLoaded={!!parkerData?.getParker}
							sublots={availableSublots}
							setShowSnackbar={setShowSnackbar}
							tenantSubscriptionId={tenantSubscriptionId}
							isEditMode={isEditMode}
							refetchParkersFromCache={refetchParkersFromCache}
							usesDefaultRates={usesDefaultRates}
							facilityId={facilityId}
						/>
					)}
					{!tenantSubscriptionId ? (
						<IntegrationDetail
							showPoolPassback={showPoolPassback}
							newPool={newPool}
							setNewPool={setNewPool}
							newPassback={newPassback}
							setNewPassback={setNewPassback}
							setHasUnsavedChanges={setHasUnsavedChanges}
							isEditMode={isEditMode}
							facilityIntegration={facilityIntegration}
							tibaActiveMType={tibaActiveMType}
							setTibaActiveMType={setTibaActiveMType}
						/>
					) : null}
					<AccessDevicesDetail
						newBillingStartDate={newBillingStartDate}
						parkerLoading={parkerLoading}
						currentDate={currentDate}
						newAccessDevices={newAccessDevices}
						setNewAccessDevices={setNewAccessDevices}
						setHasUnsavedChanges={setHasUnsavedChanges}
						isEditMode={isEditMode}
						newEmail={newEmail}
						parkerId={parkerId}
						setShowSnackbar={setShowSnackbar}
						tenantSubscriptionId={tenantSubscriptionId}
						tenantAllowedDevices={tenantAccessDevices?.getTenantEnabledAccessDevices || []}
						tenantDevicesLoading={tenantDevicesLoading}
						facilityIntegration={facilityIntegration}
						minStartDate={minStartDate}
						accessSystemCredentials={accessSystemCredentials}
					/>
					<VehiclesDetail
						newBillingStartDate={newBillingStartDate}
						parkerId={parkerId}
						newVehicles={newVehicles}
						setNewVehicles={setNewVehicles}
						onVehiclesLoaded={handleVehiclesLoaded}
						setHasUnsavedChanges={setHasUnsavedChanges}
						isEditMode={isEditMode}
						tenantSubscriptionId={tenantSubscriptionId}
						facilityIntegration={facilityIntegration}
						facilityId={facilityId}
						setShowSnackbar={setShowSnackbar}
						setVehicleHistoryChanged={setVehicleHistoryChanged}
						maxActiveVehicles={maxActiveVehicles}
						refreshKey={refreshKey}
					/>
					{!isEditMode && !tenantSubscriptionId && (
						<>
							{facilityIntegration !== 'NONE' && !isOwnerPortal && (
								<ActionsDetail
									parkerId={parkerId}
									suspensionStatus={suspensionStatus}
									terminationStatus={terminationStatus}
									accessSystemStatus={accessSystemStatus}
									setRefreshKey={setRefreshKey}
									setShowSnackbar={setShowSnackbar}
									integration={facilityIntegration}
								/>
							)}
							<StatusDetail
								parkerId={parkerId}
								parkerLoading={parkerLoading}
								currentDate={currentDate}
								setRefreshKey={setRefreshKey}
								setShowSnackbar={setShowSnackbar}
								tenantSubscriptionId={tenantSubscriptionId}
								billingDay={billingProfile?.billingDay}
								terminationNoticeDay={billingProfile?.terminationNoticeDay}
								terminationRule={billingProfile?.terminationRule}
								refetchParkersFromCache={refetchParkersFromCache}
								theme={theme}
								isWaitlistParker={isWaitlistParker}
							/>
						</>
					)}
				</Grid>
				<Grid
					className='buttons'
					container
					spacing={4}
					sx={{
						mt: 0,
						position: 'sticky',
						bottom: -15,
						zIndex: 100,
						backgroundColor: '#FFFFFF',
						pt: 0,
					}}
				>
					{!parkerLoading && (parkerIsPending || newMode) && (
						<Grid
							item
							xs={12}
							p={1}
							sx={{
								display: 'flex',
								padding: 0,
								justifyContent: 'flex-end',
								mt: 0,
								paddingTop: 0,
							}}
						>
							<Alert sx={{ color: 'black' }} severity='warning'>
								Parker will need to be Activated after saving in order for billing to start.
							</Alert>
						</Grid>
					)}
					<Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0, gap: '20px' }}>
						{isOwnerPortal ? (
							<Grid
								item
								xs={12}
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									pt: 2,
									mb: 5,
									gap: '20px',
								}}
							>
								<Button
									sx={{ border: '1px solid' }}
									variant='outlined'
									onClick={handleClose}
									data-cy='close-button'
								>
									Close
								</Button>
							</Grid>
							// biome-ignore lint/style/noNestedTernary: <>
						) : !isEditMode ? (
							<Grid
								item
								xs={12}
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									pt: 2,
									mb: 5,
									gap: '20px',
								}}
							>
								<Button variant='contained' onClick={handleEditParker} data-cy='edit-button'>
									Edit
								</Button>
								<Button
									sx={{ border: '1px solid' }}
									variant='outlined'
									onClick={handleClose}
									data-cy='close-button'
								>
									Close
								</Button>
							</Grid>
						) : (
							<Grid
								item
								xs={12}
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									gap: '20px',
									pt: 2,
									mb: 5,
								}}
							>
								<InverseButton fullWidth={false} onClick={handleCancel} name='Cancel' theme={theme} />
								<Button onClick={handleSaveClick} data-cy='save-button' disabled={saving}>
									{createLoading || updateLoading ? (
										<CircularProgress size={25} sx={{ color: 'white' }} />
									) : (
										'Save'
									)}
								</Button>
							</Grid>
						)}
					</Grid>
				</Grid>
			</Box>
			{showConfirm && (
				<Dialog open>
					<DialogContent>
						<DialogContentText>{confirmationMessage}</DialogContentText>
					</DialogContent>
					<DialogActions>
						<InverseButton
							fullWidth={false}
							onClick={() => setShowConfirm(false)}
							name='Cancel'
							theme={theme}
						/>
						<Button onClick={handleConfirm} autoFocus>
							Confirm
						</Button>
					</DialogActions>
				</Dialog>
			)}
		</>
	);
};
